import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import type { CockpitSurgeRetentionView } from '@vello/api-client';
import { formatMarketInstant, marketTimeZone } from '../../../src/index';
import { useAdmin } from '../../session';
import { useT } from '../../i18n/provider';
import { useVocab } from '../../i18n/vocab';
import { AUTO_REFRESH_MS, Badge, EmptyState, Notice, Panel, Stack, Table, Text, useAutoRefresh } from '@vello/web-theme';

/**
 * D841 (owner decision D836, ADR-0157): the surge retention in the cockpit's Zones tab - the governed policy's state and bounds, and each zone's share of the AUTOMATIC surge
 * increment retained for the Incentive Fund IN FORCE (an expired decision reads 0: fail safe), with why (the stable reason through the vocabulary), the zone state, the fund's
 * condition and when it last changed. Read-only: the bounds change through the governed-settings four-eyes flow (`incentives.surgeRetentionPolicy`). A refused read is an ERROR.
 */
const pct = (bp: number, lang: string): string => new Intl.NumberFormat(lang, { style: 'percent', maximumFractionDigits: 2 }).format(bp / 10_000);

export function SurgeRetentionPanel({ market }: { readonly market: string }): ReactElement {
  const { runtime, markets } = useAdmin();
  const { t, lang } = useT();
  const { v, fail } = useVocab();
  const tz = marketTimeZone(market, markets);
  const [view, setView] = useState<CockpitSurgeRetentionView | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const seq = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    const mine = ++seq.current;
    if (market === '') return;
    const res = await runtime.incentiveCockpit.surgeRetention(market);
    if (mine !== seq.current) return;
    if (res.ok) {
      setView(res.value);
      setError(undefined);
    } else {
      setError(t('cpt.sr.error', { code: fail(res.error) }));
    }
  }, [runtime, market, t, fail]);
  useEffect(() => {
    setView(undefined);
    void load();
  }, [load]);
  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.queue, enabled: market !== '' });

  if (error !== undefined) return <Notice tone="danger">{error}</Notice>;
  if (view !== undefined && !view.available) return <Panel><EmptyState title={t('cpt.sr.notAvailable')} /></Panel>;
  const p = view?.available === true ? view.policy : undefined;
  const zones = view?.available === true ? view.zones : [];
  return (
    <Panel padded={false}>
      <Stack gap={3}>
        <h3>{t('cpt.sr.title')}</h3>
        <Text role="caption" tone="secondary">{t('cpt.sr.subtitle')}</Text>
        {p !== undefined && (
          <Stack gap={1}>
            <Badge tone={p.state === 'ACTIVE' ? 'success' : p.state === 'KILLED' ? 'danger' : 'neutral'}>{v('surge_retention.state', p.state)}</Badge>
            {p.ceilingBp !== undefined && p.floorBp !== undefined && p.maxStepBp !== undefined && (
              <Text role="caption" tone="tertiary">
                <span className="tnum">{t('cpt.sr.bounds', { floor: pct(p.floorBp, lang), ceiling: pct(p.ceilingBp, lang), step: pct(p.maxStepBp, lang), minutes: Math.round((p.minIntervalSeconds ?? 0) / 60) })}</span>
              </Text>
            )}
          </Stack>
        )}
        <Table
          columns={[
            { key: 'zone', header: t('ctl.log.col.zone'), render: (r: (typeof zones)[number]) => <span>{r.zoneId === 'MARKET' ? t('ctl.zone.market') : r.zoneId.slice(0, 8)}</span> },
            {
              key: 'share', header: t('cpt.sr.col.share'),
              render: (r: (typeof zones)[number]) => (
                <Stack gap={1}>
                  <span className="tnum">{pct(r.retentionBp, lang)}</span>
                  {r.expired && <Text role="caption" tone="tertiary">{t('cpt.sr.expired')}</Text>}
                </Stack>
              ),
            },
            {
              key: 'why', header: t('cpt.sr.col.why'),
              render: (r: (typeof zones)[number]) => (
                <Stack gap={1}>
                  <span>{v('surge_retention.reason', r.reasonCode)}</span>
                  <Text role="caption" tone="tertiary">{v('controller.zone_state', r.zoneState)} · {v('surge_retention.fund', r.fund)}</Text>
                </Stack>
              ),
            },
            { key: 'changed', header: t('cpt.sr.col.changed'), render: (r: (typeof zones)[number]) => <span className="tnum">{formatMarketInstant(r.changedAt, tz, lang)}</span> },
          ]}
          rows={zones}
          rowKey={(r) => r.zoneId}
          empty={<EmptyState title={view === undefined ? t('ui.loading') : t('cpt.sr.empty')} />}
        />
      </Stack>
    </Panel>
  );
}
